import React from "react";

export default function PrivacyPage() {
  return (
    <div
      className="min-h-screen bg-[#FAFAF8] px-4 py-12"
      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
    >
      <div className="max-w-2xl mx-auto">
        {/* Wordmark */}
        <h1 className="text-4xl tracking-[0.25em] uppercase font-light text-[#1A1A1A] mb-1">
          Arvay
        </h1>
        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-neutral-400 font-medium mb-2">
          Privacy Policy
        </p>
        <p className="font-sans text-xs text-neutral-400 mb-12">
          Last updated: October 2026
        </p>

        <div className="space-y-10 font-sans text-sm text-[#1A1A1A] leading-relaxed">

          <section>
            <h2 className="text-base font-semibold uppercase tracking-wider mb-3">
              1. Introduction
            </h2>
            <p>
              Arvay (&ldquo;we&rdquo;, &ldquo;our&rdquo;, &ldquo;the Service&rdquo;) is a media intelligence and news aggregation
              platform accessible at arvay.live and its associated
              mobile applications. We are committed to protecting your personal data and
              handling it responsibly in accordance with applicable data protection laws,
              including the EU General Data Protection Regulation (GDPR) and India&rsquo;s
              Digital Personal Data Protection Act (DPDPA) 2023.
            </p>
            <p className="mt-3">
              This Privacy Policy explains what data we collect, why we collect it, how we
              use and protect it, and the rights you hold over your information.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold uppercase tracking-wider mb-3">
              2. Information We Collect
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
                  Account Information
                </h3>
                <p>
                  When you register, we collect your <strong>full name</strong>,{" "}
                  <strong>email address</strong>, and a <strong>bcrypt-hashed password</strong>.
                  Your password is hashed with a one-way algorithm before storage and is
                  never stored or transmitted in plain text.
                </p>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
                  Usage &amp; Preference Data
                </h3>
                <p>
                  We store articles you <strong>bookmark</strong>, <strong>keywords</strong> you
                  add to My Topics, your <strong>recent search queries</strong> (in your browser&rsquo;s
                  localStorage only — not sent to our servers), and anonymous activity
                  events (tab switches, search actions, login timestamps). These are used
                  solely to personalise your feed and improve platform performance.
                </p>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
                  Technical Data
                </h3>
                <p>
                  We collect your <strong>IP address</strong> for rate-limiting and security
                  purposes only. IP addresses are processed in memory and are not stored
                  persistently. We also capture <strong>browser type and version</strong>{" "}
                  via server request headers for compatibility diagnostics.
                </p>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
                  User-Generated Content
                </h3>
                <p>
                  Feedback submissions, source suggestions, and content reports you submit
                  through the platform are stored and reviewed by our team. These may
                  include free-text descriptions and the associated article URLs.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-base font-semibold uppercase tracking-wider mb-3">
              3. How We Use Your Information
            </h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>To create and manage your account and authenticate your sessions.</li>
              <li>To personalise your news feed, bookmarks, and topic tracking.</li>
              <li>To send transactional emails (account verification, password reset). We do not send unsolicited marketing emails.</li>
              <li>To maintain platform security, detect abuse, and enforce rate limits.</li>
              <li>To analyse aggregate, anonymised usage patterns for product improvements.</li>
              <li>To respond to feedback, reports, and support enquiries.</li>
            </ul>
            <p className="mt-3">
              We do not sell, rent, or trade your personal data to third parties. We do not
              use your data for advertising profiling or behavioural tracking.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold uppercase tracking-wider mb-3">
              4. Data Storage &amp; Security
            </h2>
            <p>
              All user data is stored in a MongoDB database hosted on secured infrastructure
              with encrypted connections (TLS/SSL). Database access is restricted to
              application services only and is not publicly accessible.
            </p>
            <p className="mt-3">
              Session tokens (JWTs) are issued upon login and stored as{" "}
              <strong>HttpOnly, Secure, SameSite=Lax cookies</strong>. For cross-browser
              compatibility, an additional copy may be stored in your browser&rsquo;s
              localStorage and transmitted via an Authorization header. Tokens expire after
              365 days of inactivity.
            </p>
            <p className="mt-3">
              Despite our safeguards, no system is 100% secure. In the event of a data
              breach affecting your personal data, we will notify you within 72 hours in
              accordance with applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold uppercase tracking-wider mb-3">
              5. Third-Party Services
            </h2>
            <p className="mb-3">
              Arvay integrates with the following external services to deliver its features.
              We do not share personally identifiable information with any of these services.
            </p>
            <div className="border border-[#E0E0E0] divide-y divide-[#E0E0E0]">
              {[
                ["GDELT Project", "gdelt.org", "News article search and discovery. Requests include only search queries — no account data."],
                ["Yahoo Finance", "finance.yahoo.com", "Real-time market data for indices and movers. No personal data transmitted."],
                ["CoinGecko", "coingecko.com", "Cryptocurrency price and market data. No personal data transmitted."],
                ["Horoscope App API", "aztro / public API", "Daily horoscope content. No personal data transmitted."],
                ["Zoho Mail SMTP", "zoho.com", "Transactional email delivery (account verification, password reset). Your email address is transmitted solely to send the relevant email."],
                ["Google Analytics", "google.com/analytics", "Aggregate, anonymised usage analytics (if enabled in your region). Subject to Google\u2019s Privacy Policy at policies.google.com/privacy."],
              ].map(([name, domain, desc]) => (
                <div key={name} className="px-4 py-3">
                  <span className="font-semibold">{name}</span>
                  <span className="text-neutral-400 text-xs ml-2">{domain}</span>
                  <p className="text-xs text-neutral-600 mt-0.5">{desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-base font-semibold uppercase tracking-wider mb-3">
              6. Cookies &amp; Local Storage
            </h2>
            <div className="space-y-3">
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1">
                  Session Cookie (<code className="bg-neutral-100 px-1 rounded">arvay_token</code>)
                </h3>
                <p>
                  An HttpOnly, Secure JWT cookie used to maintain your authenticated session.
                  It expires after 365 days. This cookie is strictly necessary for the
                  Service to function and does not require consent.
                </p>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1">
                  localStorage Keys
                </h3>
                <p>
                  We use browser localStorage to store: disclaimer acceptance status,
                  your recent search history (stored locally, never sent to our servers),
                  device session ID for analytics, and onboarding tour completion status.
                  These are not tracking cookies and are scoped entirely to your browser.
                </p>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1">
                  Analytics Cookies
                </h3>
                <p>
                  If Google Analytics is active, it may set cookies for aggregate usage
                  measurement. You can opt out via browser settings or the Google Analytics
                  opt-out extension.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-base font-semibold uppercase tracking-wider mb-3">
              7. Your Rights
            </h2>
            <p className="mb-3">
              Depending on your jurisdiction, you hold the following rights over your
              personal data:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Right of access</strong> — Request a copy of all personal data we hold about you.</li>
              <li><strong>Right to rectification</strong> — Request correction of inaccurate data (e.g., your display name).</li>
              <li><strong>Right to erasure</strong> — Request deletion of your account and all associated data. We will process this within 30 days.</li>
              <li><strong>Right to restriction</strong> — Ask us to pause processing your data while a dispute is resolved.</li>
              <li><strong>Right to data portability</strong> — Receive your data in a structured, machine-readable format (JSON).</li>
              <li><strong>Right to object</strong> — Object to processing based on legitimate interests.</li>
              <li><strong>Right to withdraw consent</strong> — Where processing is based on consent, you may withdraw it at any time.</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, email us at{" "}
              <a href="mailto:support@arvay.live" className="underline hover:text-black">
                support@arvay.live
              </a>{" "}
              with the subject line &ldquo;Privacy Request&rdquo;. We will respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold uppercase tracking-wider mb-3">
              8. Data Retention
            </h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Account data (name, email, hashed password) is retained until you request deletion.</li>
              <li>Activity logs and session data are retained for up to 90 days, then automatically purged.</li>
              <li>Bookmarks and topic keywords are retained until you delete them or close your account.</li>
              <li>Feedback, reports, and source suggestions are retained for up to 12 months for product improvement purposes.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold uppercase tracking-wider mb-3">
              9. Children&rsquo;s Privacy &amp; Age Restriction
            </h2>
            <p>
              <strong>Arvay is restricted to users aged 17 and above.</strong> The platform aggregates
              news, financial data, and community content that may include mature themes not suitable
              for younger audiences. Users must confirm they are at least 17 years old upon first
              login. We do not knowingly collect personal data from individuals under the age of 17.
              If you believe a user under 17 has registered, please contact us at{" "}
              <a href="mailto:support@arvay.live" className="underline hover:text-black">support@arvay.live</a>{" "}
              and we will delete the account promptly.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold uppercase tracking-wider mb-3">
              10. Data Storage and Hosting
            </h2>
            <p>
              Your account data and application data are stored on servers located in the United
              States. The frontend is served via a global content delivery network, so visitors in
              India load pages from a nearby edge location, but your data resides in the US.
            </p>
            <p className="mt-3">
              The platform uses industry-standard security practices including encryption in transit
              (TLS) and at rest (AES-256). Arvay does not currently hold a specific India
              data-residency attestation or DPDP Act compliance certificate. By using this platform,
              you acknowledge that your data is processed and stored in the United States.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold uppercase tracking-wider mb-3">
              11. International Data Transfers
            </h2>
            <p>
              Arvay&rsquo;s servers are located in the EU/IN region. If you access the Service
              from outside these regions, your data may be transferred across borders. We
              ensure such transfers comply with applicable data protection law through
              appropriate safeguards.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold uppercase tracking-wider mb-3">
              12. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. When we do, we will
              revise the &ldquo;Last updated&rdquo; date at the top of this page and, where changes are
              material, notify you by email or a prominent platform notice. Your continued
              use of the Service after any changes constitutes acceptance of the updated
              Policy.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold uppercase tracking-wider mb-3">
              13. Contact &amp; Data Controller
            </h2>
            <div className="bg-neutral-50 border border-[#E0E0E0] p-5 space-y-1">
              <p>
                Email:{" "}
                <a href="mailto:support@arvay.live" className="underline hover:text-black">
                  support@arvay.live
                </a>
              </p>
              <p>Subject line: &ldquo;Privacy Request&rdquo; or &ldquo;Data Enquiry&rdquo;</p>
            </div>
          </section>

        </div>

        {/* Back nav */}
        <div className="mt-14 pt-8 border-t border-[#E0E0E0] flex flex-col sm:flex-row gap-4 justify-between items-start font-sans text-xs text-neutral-400">
          <div className="space-x-4">
            <a href="/" className="underline hover:text-black transition-colors">Back to Arvay</a>
            <a href="/disclaimer" className="underline hover:text-black transition-colors">Legal Disclaimer</a>
            <a href="/terms" className="underline hover:text-black transition-colors">Terms of Use</a>
          </div>
          <span>&copy; 2026 Arvay. All rights reserved.</span>
        </div>
      </div>
    </div>
  );
}
